import { create } from 'zustand'
import { produce } from 'immer'

type SiteData = {
  dailyVisitors: number | null
  monthlyVisitors: number | null
  alexaRank: number | null
}

type SiteDataResponse = {
  alexa: string
  daily: string
  monthly: string
}

type SiteStore = {
  siteData: Record<string, SiteData | null | undefined>
  updateSite: (domain: string, data: SiteData | null) => void
}

const useSiteDataStore = create<SiteStore>((set) => ({
  siteData: {},
  updateSite: (domain, data) =>
    set(
      produce((state: SiteStore) => {
        state.siteData[domain] = data
      }),
    ),
}))

const getUrl = (domain: string) => `/external/sitetraffic?domain=${encodeURIComponent(domain)}`

const parseFormattedInt = (str: string): number | null => {
  const parseable = str.replace(/,/g, '')
  const value = parseInt(parseable, 10)
  return isNaN(value) ? null : value
}

const fetchSiteData = (domain: string): Promise<SiteData> => {
  return new Promise((resolve, reject) => {
    fetch(getUrl(domain))
      .then(response => response.json())
      .then((data: SiteDataResponse) => {
        const alexaRank = parseFormattedInt(data.alexa)
        const dailyVisitors = parseFormattedInt(data.daily)
        const monthlyVisitors = parseFormattedInt(data.monthly)
        resolve({
          alexaRank,
          dailyVisitors,
          monthlyVisitors,
        })
      })
      .catch(reject)
  })
}

export const useSiteData = (domain: string): SiteData | null => {
  const data = useSiteDataStore((state) => state.siteData[domain])
  const updateSite = useSiteDataStore((state) => state.updateSite)

  if (data !== undefined) {
    return data
  }

  updateSite(domain, null);
  fetchSiteData(domain)
    .then(data => updateSite(domain, data))

  return null
}